import {
  Button,
  Card,
  Drawer,
  Grid,
  Space,
  Spin,
  Tag,
  Transfer,
  Typography,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import ReportSelectorPanel, {
  getDistributionType,
  TransferItem,
} from "./ReportSelectorPanel";
import { Key } from "antd/lib/table/interface";

const { useBreakpoint } = Grid;

export interface DownloadJsonsDrawerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
  isLoading: boolean;
  dataSource: TransferItem[];
}

export function DownloadJsonsDrawer({
  open,
  onClose,
  onConfirm,
  isLoading,
  dataSource,
}: DownloadJsonsDrawerProps) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [targetKeys, setTargetKeys] = useState<string[]>(() =>
    dataSource.filter((i) => !i.disabled).map((i) => i.key),
  );

  useMemo(() => {
    setTargetKeys(dataSource.filter((i) => !i.disabled).map((i) => i.key));
  }, [dataSource]);

  function handleTransferChange(newKeys: Key[]) {
    // No debemos mover items bloqueados al panel de descaga
    const safe = newKeys.filter((k) => {
      const item = dataSource.find((d) => d.key === k);
      return item && !item.disabled;
    });
    setTargetKeys(safe as string[]);
  }

  // Contadores de los seleccionados
  const selectedItems = useMemo(
    () => dataSource.filter((i) => targetKeys.includes(i.key)),
    [dataSource, targetKeys],
  );
  const plantas = selectedItems.filter(
    (i) => getDistributionType(i.senerLicense) === "Planta",
  ).length;
  const estaciones = selectedItems.length - plantas;

  const mobilePageSize = screens.sm ? 4 : 3;
  const desktopTableHeight = "calc(100vh - 260px)";

  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");

  // Seleccion solo para movil
  const [mobileLeftSelected, setMobileLeftSelected] = useState<string[]>([]);
  const [mobileRightSelected, setMobileRightSelected] = useState<string[]>([]);

  const targetSet = new Set(targetKeys);
  const leftItems = dataSource.filter((i) => !targetSet.has(i.key));
  const rightItems = dataSource.filter((i) => targetSet.has(i.key));

  function moveToRight() {
    const eligible = mobileLeftSelected.filter((k) => {
      const item = dataSource.find((d) => d.key === k);
      return item && !item.disabled;
    });
    handleTransferChange([...new Set([...targetKeys, ...eligible])]);
    setMobileLeftSelected([]);
  }

  function moveToLeft() {
    const next = targetKeys.filter((k) => !mobileRightSelected.includes(k));
    handleTransferChange(next);
    setMobileRightSelected([]);
  }

  const titles = useMemo(
    () => [
      `No se descargarán (${dataSource.length - targetKeys.length})`,
      `Se descargarán (${targetKeys.length})`,
    ],
    [dataSource, targetKeys],
  );

  return (
    <Drawer
      title="Reportes Json Mensuales a Descargar"
      placement="right"
      width={isMobile ? "100%" : "90vw"}
      open={open}
      onClose={onClose}
      styles={{
        body: { padding: 16, display: "flex", flexDirection: "column" },
      }}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <Space>
            <Typography.Text>
              Total: <strong>{selectedItems.length}</strong>
            </Typography.Text>
            <Tag color="blue">Plantas: {plantas}</Tag>
            <Tag color="green">Estaciones: {estaciones}</Tag>
          </Space>

          <Space>
            <Button onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => onConfirm(targetKeys)}
              loading={isLoading}
              disabled={targetKeys.length === 0}
              size="large"
            >
              Descargar ({targetKeys.length})
            </Button>
          </Space>
        </div>
      }
    >
      <Spin spinning={isLoading}>
        {isMobile && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Card
              title={<Typography.Text>{titles[1]}</Typography.Text>}
              size="small"
            >
              <ReportSelectorPanel
                direction="right"
                items={rightItems}
                search={rightSearch}
                onSearchChange={setRightSearch}
                selectedKeys={mobileRightSelected}
                onItemSelect={(key, selected) =>
                  setMobileRightSelected((prev) =>
                    selected ? [...prev, key] : prev.filter((k) => k !== key),
                  )
                }
                onItemSelectAll={(keys, selected) =>
                  setMobileRightSelected((prev) =>
                    selected
                      ? [...new Set([...prev, ...keys])]
                      : prev.filter((k) => !keys.includes(k)),
                  )
                }
                pageSize={mobilePageSize}
              />
            </Card>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Button
                block
                icon={<ArrowDownOutlined />}
                onClick={moveToLeft}
                disabled={mobileRightSelected.length === 0}
              >
                Quitar de descarga ({mobileRightSelected.length} seleccionados)
              </Button>
              <Button
                block
                type="primary"
                icon={<ArrowUpOutlined />}
                onClick={moveToRight}
                disabled={mobileLeftSelected.length === 0}
              >
                Agregar a descarga ({mobileLeftSelected.length} seleccionados)
              </Button>
            </div>

            <Card
              title={<Typography.Text>{titles[0]}</Typography.Text>}
              size="small"
            >
              <ReportSelectorPanel
                direction="left"
                items={leftItems}
                search={leftSearch}
                onSearchChange={setLeftSearch}
                selectedKeys={mobileLeftSelected}
                onItemSelect={(key, selected) =>
                  setMobileLeftSelected((prev) =>
                    selected ? [...prev, key] : prev.filter((k) => k !== key),
                  )
                }
                onItemSelectAll={(keys, selected) =>
                  setMobileLeftSelected((prev) =>
                    selected
                      ? [...new Set([...prev, ...keys])]
                      : prev.filter((k) => !keys.includes(k)),
                  )
                }
                pageSize={mobilePageSize}
              />
            </Card>
          </div>
        )}

        {!isMobile && (
          <Transfer<TransferItem>
            dataSource={dataSource}
            targetKeys={targetKeys}
            onChange={handleTransferChange}
            titles={titles}
            showSelectAll={false}
            style={{ width: "100%" }}
            listStyle={{ width: "50%", height: desktopTableHeight }}
            rowKey={(item) => item.key}
          >
            {({
              direction,
              filteredItems,
              onItemSelect,
              onItemSelectAll,
              selectedKeys,
            }) => (
              <ReportSelectorPanel
                direction={direction}
                items={filteredItems}
                search={direction === "left" ? leftSearch : rightSearch}
                onSearchChange={
                  direction === "left" ? setLeftSearch : setRightSearch
                }
                selectedKeys={selectedKeys as string[]}
                onItemSelect={onItemSelect}
                onItemSelectAll={onItemSelectAll}
              />
            )}
          </Transfer>
        )}
      </Spin>
    </Drawer>
  );
}
